import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  AddTeamIcon,
  DeleteIcon,
  EditProgramIcon,
  RefreshIcon,
} from "../assets/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToAddComment,
  reqToDeleteComment,
  reqToGetFeedbackById,
  reqToUpdateComment,
  reqToAddVote,
  reqToUpdateFeedbackStatus,
  reqToUpdateFeedbackData,
} from "../redux/slices.js/feedback/feedBackSlice";
import { toast } from "react-toastify";

const FeedbackDetailsModal = ({ isOpen, onClose, feedbackItem }) => {
  const dispatch = useDispatch();
  const [statusUpdate, setStatusUpdate] = useState("Under Review");
  const [comment, setComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({
    status: null,
    assignee: null,
    priority: null,
  });
  const staffList = useSelector((state) => state.staff.staffList);
  const types = useSelector((state) => state?.feedbackslice?.type);

  const { priority } = types || [];
  const statusList = useSelector((state) => state.feedbackslice.status);

  const detailedFeedback = useSelector(
    (state) => state.feedbackslice.feedbackDetail
  );
  const loading = useSelector((state) => state.feedbackslice.loading);

  const sortOptions = useMemo(() => {
    const prioritySortOptions = (priority || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown Priority",
    }));

    return [...prioritySortOptions];
  }, [priority]);

  const staffOptions = useMemo(() => {
    return (staffList || [])
      ?.filter((staff) => staff.status === "active")
      ?.map((staff) => ({
        value: staff.id?.toString() || "",
        label: staff.name || "Unknown Staff",
        originalData: staff, // Keep original data if needed
      }));
  }, [staffList]);

  useEffect(() => {
    if (isOpen && feedbackItem?.id) {
      dispatch(
        reqToGetFeedbackById({
          id: feedbackItem.id,
          onSuccess: (res) => {
            if (res.data?.status) {
              const statusText = getStatusText(res.data.status);
              setStatusUpdate(statusText);
            }
          },
          onFailure: (err) => {
            console.error("Error fetching feedback details:", err);
          },
        })
      );
    }
  }, [isOpen, feedbackItem?.id, dispatch]);

  const handleEditComment = (commentItem) => {
    setEditingCommentId(commentItem.id);
    setEditCommentText(commentItem.comments);
  };

  const handleSaveEdit = (commentId) => {
    if (!editCommentText.trim()) {
      alert("Please enter a comment");
      return;
    }

    dispatch(
      reqToUpdateComment({
        id: commentId,
        data: { comment: editCommentText },
        onSuccess: (res) => {
          setEditingCommentId(null);
          setEditCommentText("");
          dispatch(
            reqToGetFeedbackById({
              id: feedbackItem.id,
              onSuccess: (res) => {},
              onFailure: (err) => {
                console.error("Error refreshing feedback details:", err);
              },
            })
          );
        },
        onFailure: (err) => {
          console.error("Error updating comment:", err);
        },
      })
    );
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentText("");
  };

  const handleDeleteComment = (commentId) => {
    setDeletingCommentId(commentId);
    dispatch(
      reqToDeleteComment({
        id: commentId,
        onSuccess: (res) => {
          toast.success("Comment deleted successfully");
          setDeletingCommentId(null);
          dispatch(
            reqToGetFeedbackById({
              id: feedbackItem.id,
              onSuccess: (res) => {},
              onFailure: (err) => {
                console.error("Error refreshing feedback details:", err);
              },
            })
          );
        },
        onFailure: (err) => {
          console.error("Error deleting comment:", err);
          setDeletingCommentId(null);
        },
      })
    );
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      // If currently in edit mode, cancel and reset fields
      setEditedFields({
        status: null,
        assignee: null,
        priority: null,
      });
    }
    setIsEditMode(!isEditMode);
  };

  const handleFieldChange = (field, value) => {
    setEditedFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateFields = () => {
    // Prepare data for API call with the required format
    const updateData = {
      action: "all",
    };

    // Add priority_value if priority is edited
    if (editedFields.priority !== null) {
      updateData.priority = parseInt(editedFields.priority);
    }

    // Add status_value if status is edited
    if (editedFields.status !== null) {
      const statusCode = getStatusCode(editedFields.status);
      updateData.status = statusCode;
    }

    // Add assignee if assignee is edited
    if (editedFields.assignee !== null) {
      updateData.assigned_to = parseInt(editedFields.assignee);
    }

    // Call the update API with the new format
    dispatch(
      reqToUpdateFeedbackData({
        id: feedbackItem.id,
        data: updateData,
        onSuccess: (res) => {
          // Reset edit mode on success
          setIsEditMode(false);
          setEditedFields({
            status: null,
            assignee: null,
            priority: null,
          });
          // Refresh the data
          dispatch(
            reqToGetFeedbackById({
              id: feedbackItem.id,
              onSuccess: (res) => {
                if (res.data?.status) {
                  const statusText = getStatusText(res.data.status);
                  setStatusUpdate(statusText);
                }
              },
              onFailure: (err) => {
                console.error("Error refreshing feedback details:", err);
              },
            })
          );
          toast.success("Fields updated successfully");
        },
        onFailure: (err) => {
          console.error("Error updating fields:", err);
        },
      })
    );
  };
  const handleAddComment = () => {
    if (!comment.trim()) {
      alert("Please enter a comment");
      return;
    }

    setAddingComment(true);
    dispatch(
      reqToAddComment({
        id: feedbackItem.id,
        data: { comment: comment },
        onSuccess: (res) => {
          setComment("");
          dispatch(
            reqToGetFeedbackById({
              id: feedbackItem.id,
              onSuccess: (res) => {},
              onFailure: (err) => {
                console.error("Error refreshing feedback details:", err);
              },
            })
          );
        },
        onFailure: (err) => {
          console.error("Error adding comment:", err);
        },
      })
    ).finally(() => {
      setAddingComment(false);
    });
  };

  const getStatusCode = (statusText) => {
    if (!statusList || statusList.length === 0) return 2;

    const statusItem = statusList.find((item) => item.name === statusText);
    return statusItem ? statusItem.id : 2;
  };

  const getStatusText = (statusCode) => {
    if (!statusList || statusList.length === 0) return "New";

    const statusItem = statusList.find((item) => item.id === statusCode);
    return statusItem ? statusItem.name : "New";
  };

  const getPriorityText = (priorityCode) => {
    const priorityMapping = {
      1: "Low",
      2: "Medium",
      3: "High",
    };
    return priorityMapping[priorityCode] || "Medium";
  };

  const getTypeText = (typeCode) => {
    const typeMapping = {
      1: "Bug",
      2: "Idea",
      3: "CSAT",
    };
    return typeMapping[typeCode] || "Idea";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const renderAssignedStaff = () => {
    if (!detailedFeedback.assignedStaff) {
      return "Unassigned";
    }

    if (Array.isArray(detailedFeedback.assignedStaff)) {
      if (detailedFeedback.assignedStaff.length === 0) {
        return "Unassigned";
      }
      return detailedFeedback.assignedStaff
        .map((staff) => staff.name || `User ID: ${staff.id}`)
        .join(", ");
    } else {
      return (
        detailedFeedback.assignedStaff.name ||
        `User ID: ${detailedFeedback.assignedStaff.id}`
      );
    }
  };

  const statusOptions =
    statusList && statusList.length > 0
      ? statusList.map((status) => ({
          value: status.name,
          label: status.name,
        }))
      : [
          { value: "New", label: "New" },
          { value: "Under Review", label: "Under Review" },
          { value: "Planned", label: "Planned" },
          { value: "In Progress", label: "In Progress" },
          { value: "Released", label: "Released" },
          { value: "Closed", label: "Closed" },
        ];

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-[9999] transition-all duration-300 bg-black/20 backdrop-blur-sm ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      ></div>
      <div
        className={`fixed inset-0 z-[9999] transition-all duration-300 h-screen max-w-[658px] w-full top-0 right-0 left-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="bg-white shadow-2xl w-full h-full overflow-y-auto">
          <div className="bg-blue-500 text-white p-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold m-0 Rubik">
              Feedback details
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors w-[40px] h-[40px] flex items-center justify-center bg-[#F6F6F6] rounded-full"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="#646363"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-4 md:p-6 space-y-6">
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <div className="text-span text-black">
                  Loading feedback details...
                </div>
              </div>
            ) : !detailedFeedback ? (
              <div className="flex justify-center items-center py-10">
                <div className="text-span text-red-500">
                  Failed to load feedback details
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1">
                      <h3 className="text-p !font-medium text-black ">
                        {detailedFeedback.title || feedbackItem.title}
                      </h3>
                      <p className="text-sub_span text-[#6D6D6D] mb-0">
                        {detailedFeedback.description ||
                          "No description provided."}
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex justify-end mt-3">
                        <button
                          className="btn-light-gray !text-[#4285F4]"
                          onClick={handleEditToggle}
                        >
                          {RefreshIcon}
                          {isEditMode ? "Cancel" : "Edit"}
                        </button>
                        {isEditMode && (
                          <button
                            className="btn-light-blue"
                            onClick={handleUpdateFields}
                          >
                            {AddTeamIcon}
                            Update All
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-2">
                    <div className="px-2 py-1 min-w-[100px] inline-block text-center rounded-full text-sub_span bg-[#FAFAFA] text-[#5C5B62]">
                      {getTypeText(detailedFeedback.type)}
                    </div>
                    <div className="px-2 py-1 min-w-[100px] inline-block text-center rounded-full text-sub_span bg-[#F6FFED] text-[#277600]">
                      {detailedFeedback?.total_votes} votes
                    </div>
                    <div
                      className={`px-2 py-1 min-w-[75px] inline-block text-center rounded-full text-sub_span ${
                        detailedFeedback.priority === 3
                          ? "bg-[#FFE4D7] text-[#F45C0C]"
                          : detailedFeedback.priority === 2
                          ? "bg-[#FFFBD7] text-[#F4B40C]"
                          : "bg-[#E4F7FF] text-[#0C8FF4]"
                      }`}
                    >
                      {getPriorityText(detailedFeedback.priority)}
                    </div>
                    <div className="px-2 py-1 min-w-[100px] inline-block text-center rounded-full text-sub_span bg-[#E3EDFF] text-[#4285F4]">
                      {getStatusText(detailedFeedback.status)}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border-[1px] border-[#ECF3FF]">
                  <div className="flex items-stretch border-b-[1px] border-[#ECF3FF]">
                    <div className="md:w-1/3 w-[45%] flex-none px-4 py-5 bg-[#F1F6FF] text-body text-black">
                      Submitted by:
                    </div>
                    <div className="px-4 py-5 text-body text-black">
                      {detailedFeedback.user?.name || "Unknown"} (
                      {detailedFeedback.user?.email || "No email"})
                    </div>
                  </div>
                  <div className="flex items-stretch border-b-[1px] border-[#ECF3FF]">
                    <div className="md:w-1/3 w-[45%] flex-none px-4 py-5 bg-[#F1F6FF] text-body text-black">
                      Tenant:
                    </div>
                    <div className="px-4 py-5 text-body text-black">
                      {detailedFeedback.user?.name || "Unknown"}
                    </div>
                  </div>
                  <div className="flex items-stretch border-b-[1px] border-[#ECF3FF]">
                    <div className="md:w-1/3 w-[45%] flex-none px-4 py-5 bg-[#F1F6FF] text-body text-black">
                      Submitted At:
                    </div>
                    <div className="px-4 py-5 text-body text-black">
                      {formatDate(detailedFeedback.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-stretch border-b-[1px] border-[#ECF3FF]">
                    <div className="md:w-1/3 w-[45%] flex-none px-4 py-5 bg-[#F1F6FF] text-body text-black">
                      Assignee:
                    </div>

                    <div className="px-4 py-5 text-body text-black flex items-center w-full comman-form">
                      {isEditMode ? (
                        <div className="w-[100%] form-group">
                          <Select
                            options={staffOptions}
                            value={staffOptions.find(
                              (option) =>
                                option.value ===
                                (editedFields.assignee !== null
                                  ? editedFields.assignee
                                  : detailedFeedback.assignedStaff?.id?.toString())
                            )}
                            onChange={(selectedOption) =>
                              handleFieldChange(
                                "assignee",
                                selectedOption?.value
                              )
                            }
                            isSearchable={false}
                            placeholder="Select an assignee"
                          />
                        </div>
                      ) : (
                        renderAssignedStaff()
                      )}
                    </div>
                  </div>
                  <div className="flex items-stretch">
                    <div className="md:w-1/3 w-[45%] flex-none px-4 py-5 bg-[#F1F6FF] text-body text-black">
                      Votes:
                    </div>
                    <div className="px-4 py-5 text-body text-black flex items-center gap-2">
                      <div className="px-2 py-1 min-w-[100px] inline-block text-center rounded-full text-sub_span bg-[#FAFAFA] text-[#5C5B62]">
                        {detailedFeedback?.total_votes} Total votes
                      </div>
                      <div className="px-2 py-1 min-w-[100px] inline-block text-center rounded-full text-sub_span bg-[#F6FFED] text-[#277600]">
                        {detailedFeedback?.up_votes} Positive votes
                      </div>
                      <div className="px-2 py-1 min-w-[75px] inline-block text-center rounded-full text-sub_span bg-[#FFE4D7] text-[#F45C0C]">
                        {detailedFeedback?.down_votes} Negative votes
                      </div>
                    </div>
                  </div>
                </div>

                <div className="comman-form">
                  <div className="form-group">
                    <label>
                      Status Update <span>*</span>
                    </label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(
                        (option) =>
                          option.value ===
                          (isEditMode && editedFields.status !== null
                            ? editedFields.status
                            : statusUpdate)
                      )}
                      onChange={(selectedOption) =>
                        isEditMode
                          ? handleFieldChange("status", selectedOption?.value)
                          : setStatusUpdate(selectedOption?.value)
                      }
                      isDisabled={!isEditMode}
                      placeholder="Select status"
                    />
                  </div>
                </div>

                <div className="comman-form">
                  <div className="form-group">
                    <label>
                      Priority Update <span>*</span>
                    </label>
                    <Select
                      options={sortOptions}
                      value={sortOptions.find(
                        (option) =>
                          option.value ===
                          (isEditMode && editedFields.priority !== null
                            ? editedFields.priority
                            : detailedFeedback.priority?.toString())
                      )}
                      onChange={(selectedOption) =>
                        isEditMode &&
                        handleFieldChange("priority", selectedOption?.value)
                      }
                      isDisabled={!isEditMode}
                      placeholder="Select priority"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-span !font-medium text-black">
                    Comments ({detailedFeedback.comments?.length || 0})
                  </h4>

                  {detailedFeedback.comments &&
                  detailedFeedback.comments.length > 0 ? (
                    detailedFeedback.comments.map((commentItem) => (
                      <div
                        key={commentItem.id}
                        className="p-4 bg-[#FAFAFA] rounded-lg relative"
                      >
                        <div className="absolute top-3 right-3 flex gap-2">
                          {commentItem?.is_my_comment && (
                            <button
                              onClick={() => handleEditComment(commentItem)}
                              className="text-blue-500 hover:text-blue-700 transition-colors"
                              disabled={editingCommentId || deletingCommentId}
                            >
                              {EditProgramIcon}
                            </button>
                          )}
                          {commentItem?.is_my_comment && (
                            <button
                              onClick={() =>
                                handleDeleteComment(commentItem.id)
                              }
                              className="text-red-500 hover:text-red-700 transition-colors"
                              disabled={editingCommentId || deletingCommentId}
                            >
                              {deletingCommentId === commentItem.id ? (
                                <span className="text-sm">Deleting...</span>
                              ) : (
                                DeleteIcon
                              )}
                            </button>
                          )}
                        </div>

                        <div className="flex gap-3 mb-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#DBE9FF] rounded-full flex items-center justify-center text-blue-100 text-span !font-medium uppercase">
                            {commentItem.user?.name?.charAt(0) || "U"}
                          </div>
                          <div className="flex items-start gap-1 flex-col">
                            <span className="text-sub_span !font-semibold text-black">
                              {commentItem.user?.email || "Unknown user"}
                            </span>
                            <span className="text-sub_span text-[#9BA2AB]">
                              {formatDate(commentItem.createdAt)}
                            </span>
                          </div>
                        </div>

                        {editingCommentId === commentItem.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editCommentText}
                              onChange={(e) =>
                                setEditCommentText(e.target.value)
                              }
                              placeholder="Edit your comment"
                              rows={3}
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveEdit(commentItem.id)}
                                className="btn-light-blue text-sm"
                                disabled={!editCommentText.trim()}
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="btn-light-gray text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sub_span !font-semibold text-black mb-0">
                            {commentItem.comments}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-4 bg-[#FAFAFA] rounded-lg text-center">
                      <p className="text-sub_span text-[#6D6D6D] mb-0">
                        No comments yet.
                      </p>
                    </div>
                  )}
                </div>
                <div className="comman-form">
                  <div className="form-group">
                    <label>
                      Add Comment <span>*</span>
                    </label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add a comment"
                      rows={6}
                      disabled={addingComment}
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    className="btn-light-blue"
                    onClick={handleAddComment}
                    disabled={addingComment || !comment.trim()}
                  >
                    {addingComment ? "Adding..." : "Add comment"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FeedbackDetailsModal;
